import type { ColumnDef } from '@tanstack/react-table';
import styled from 'styled-components';

import {
    BundleState,
    ContainerType,
    GetBundlesFilterQuery,
    GetSitesQuery,
    GetToteamUsersQuery,
    GetWarehouseColumnsQuery,
    ProductFlow,
} from 'data/__generated__';

import { dateRangeColumn, multiSelectColumn, numberColumn } from 'components/DataTable';
import { DetailLink } from 'components/DetailsView/DetailLink';

import { getExtendedFormattedDatetime } from 'helpers/dateTimes';

import { BundleTypeTag } from 'pages/Bundles/components/TypeTag';
import { ContainerTypeTag } from 'pages/Bundles/components/ContainerTypeTag';
import { StatusTag } from 'pages/Bundles/components/StatusTag';
import { BUNDLE_STATES_LABELS } from 'pages/Bundles/constants/states';
import { BUNDLE_TYPES_LABELS } from 'pages/Bundles/constants/types';

export type BundleRow = GetBundlesFilterQuery['bundlesFilter']['bundles'][number];

export type BundleFilterInput = {
    searchValue?: string;
    state?: BundleState[];
    type?: ProductFlow[];
    containerType?: ContainerType[];
    operatorIds?: string[];
    warehouseColumnIds?: string[];
    siteIds?: string[];
    scheduledPreparationDate?: { gte?: number; lte?: number };
    createdAt?: { gte?: number; lte?: number };
};

const CONTAINER_TYPE_LABELS: Record<ContainerType, string> = {
    [ContainerType.Box]: 'Box',
    [ContainerType.Palette]: 'Palette',
    [ContainerType.Kangabox]: 'Kangabox',
    [ContainerType.Parcel]: 'Colis',
    [ContainerType.FreshParcel]: 'Colis frais',
    [ContainerType.Kraft]: 'Kraft',
};

type BuildColumnsArgs = {
    operators: GetToteamUsersQuery['toteamUsers'];
    warehouseColumns: GetWarehouseColumnsQuery['warehouseColumns'];
    sites: GetSitesQuery['sites'];
};

// A bundle can group boxes from several sites — list each distinct site once.
function getBundleSiteNames(row: BundleRow): string {
    const names = [...new Set(row.boxes.map((box) => box.siteName).filter((name): name is string => !!name))];
    return names.length ? names.join(', ') : '-';
}

export function buildBundlesColumns({
    operators,
    warehouseColumns,
    sites,
}: BuildColumnsArgs): ColumnDef<BundleRow, unknown>[] {
    return [
        numberColumn<BundleRow, BundleFilterInput>({
            accessorKey: 'number',
            header: 'N°',
            filterKey: 'searchValue',
            placeholder: 'Numéro',
            defaultVisible: true,
            enableServerSort: true,
        }),
        dateRangeColumn<BundleRow, BundleFilterInput>({
            accessorKey: 'createdAt',
            header: 'Créé le',
            filterKey: 'createdAt',
            defaultVisible: true,
            enableServerSort: true,
            cell: ({ row }) => getExtendedFormattedDatetime(row.original.createdAt),
        }),
        dateRangeColumn<BundleRow, BundleFilterInput>({
            accessorKey: 'scheduledPreparationDate',
            header: 'Préparation prévue',
            filterKey: 'scheduledPreparationDate',
            defaultVisible: false,
            enableServerSort: true,
            cell: ({ row }) => row.original.scheduledPreparationDate,
        }),
        multiSelectColumn<BundleRow, BundleFilterInput>({
            id: 'warehouseColumnName',
            accessorFn: (row) => row.warehouseColumn?.name ?? '-',
            header: 'Entrepôt',
            filterKey: 'warehouseColumnIds',
            defaultVisible: false,
            options: warehouseColumns.map((column) => ({
                value: column._id,
                label: column.name,
            })),
            cell: ({ row }) => row.original.warehouseColumn?.name ?? '-',
        }),
        multiSelectColumn<BundleRow, BundleFilterInput>({
            id: 'operatorName',
            accessorFn: (row) => row.operatorName ?? '-',
            header: 'Opérateur.ice',
            filterKey: 'operatorIds',
            defaultVisible: false,
            options: operators
                .map((user) => ({
                    value: user._id,
                    label: user.profile.fullname ?? user._id,
                }))
                .sort((a, b) => a.label.localeCompare(b.label)),
            cell: ({ row }) => row.original.operatorName ?? '-',
        }),
        multiSelectColumn<BundleRow, BundleFilterInput>({
            id: 'siteName',
            accessorFn: (row) => getBundleSiteNames(row),
            header: 'Site',
            filterKey: 'siteIds',
            defaultVisible: false,
            options: sites
                .map((site) => ({ value: site._id, label: site.name }))
                .sort((a, b) => a.label.localeCompare(b.label)),
            cell: ({ row }) => getBundleSiteNames(row.original),
        }),
        multiSelectColumn<BundleRow, BundleFilterInput>({
            accessorKey: 'type',
            header: 'Type',
            filterKey: 'type',
            defaultVisible: true,
            options: Object.values(ProductFlow).map((flow) => ({
                value: flow,
                label: BUNDLE_TYPES_LABELS[flow] ?? flow,
            })),
            renderOption: (option) => <BundleTypeTag type={option.value as ProductFlow} />,
            cell: ({ row }) => <BundleTypeTag type={row.original.type} />,
        }),
        multiSelectColumn<BundleRow, BundleFilterInput>({
            accessorKey: 'containerType',
            header: 'Container',
            filterKey: 'containerType',
            defaultVisible: false,
            options: Object.values(ContainerType).map((containerType) => ({
                value: containerType,
                label: CONTAINER_TYPE_LABELS[containerType] ?? containerType,
            })),
            renderOption: (option) => <ContainerTypeTag type={option.value as ContainerType} />,
            cell: ({ row }) => <ContainerTypeTag type={row.original.containerType} />,
        }),
        {
            id: 'boxes',
            header: 'Caisses',
            meta: { defaultVisible: true },
            cell: ({ row }) => (
                <Boxes>
                    {[...row.original.boxes]
                        .sort((boxA, boxB) => (boxA.position || 0) - (boxB.position || 0))
                        .map(({ _id, label, deliveryId, position }) => (
                            <BoxChip key={_id}>
                                {position !== null && position !== undefined ? (
                                    <PositionBadge title="Position dans le bundle">#{position}</PositionBadge>
                                ) : null}
                                <DetailLink path="delivery" value={deliveryId} name={label} shouldWrap />
                            </BoxChip>
                        ))}
                </Boxes>
            ),
        },
        {
            id: 'stats',
            header: 'Stats',
            meta: { defaultVisible: true },
            cell: ({ row }) => {
                const {
                    numberOfReferences,
                    numberOfUnits,
                    operatorName,
                    preparationDates: { start, end },
                    preparationDuration,
                    stateHistory,
                } = row.original;
                const durationString = preparationDuration
                    ? `${Math.floor(preparationDuration / 60)}h ${(preparationDuration % 60)
                          .toString()
                          .padStart(2, '0')}`
                    : '-';
                const hasBeenPaused = stateHistory.some((entry) => entry.state === BundleState.Paused);
                return (
                    <StatsCell>
                        <StatLine>
                            <StatValue>{numberOfReferences}</StatValue>
                            <StatLabel>réfs</StatLabel>
                            <StatSeparator>·</StatSeparator>
                            <StatValue>{numberOfUnits}</StatValue>
                            <StatLabel>unités</StatLabel>
                        </StatLine>
                        <StatMetaLine>
                            <StatLabel>Op.</StatLabel>
                            <StatMetaValue>{operatorName ?? '-'}</StatMetaValue>
                        </StatMetaLine>
                        <StatMetaLine>
                            <StatMetaValue>
                                {start || '?'}&nbsp;&rarr;&nbsp;{end || '?'}
                            </StatMetaValue>
                            <StatSeparator>·</StatSeparator>
                            <StatMetaValue>{durationString}</StatMetaValue>
                        </StatMetaLine>
                        {hasBeenPaused ? <PausedWarning>A été "paused"</PausedWarning> : null}
                    </StatsCell>
                );
            },
        },
        multiSelectColumn<BundleRow, BundleFilterInput>({
            accessorKey: 'state',
            header: 'Statut',
            filterKey: 'state',
            defaultVisible: true,
            options: Object.values(BundleState).map((state) => ({
                value: state,
                label: BUNDLE_STATES_LABELS[state] ?? state,
            })),
            renderOption: (option) => <StatusTag state={option.value as BundleState} />,
            cell: ({ row }) => <StatusTag state={row.original.state} />,
        }),
    ];
}

const Boxes = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
`;

const BoxChip = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
`;

const PositionBadge = styled.span`
    flex-shrink: 0;
    font-size: 11px;
    font-weight: 700;
    color: ${({ theme }) => theme.ctaPrimaryColor};
`;

const PausedWarning = styled.div`
    font-size: 12px;
    color: ${({ theme }) => theme.warningColor};
`;

const StatsCell = styled.div`
    display: flex;
    flex-direction: column;
    gap: 1px;
    white-space: nowrap;
`;

const StatLine = styled.div`
    display: flex;
    align-items: baseline;
    gap: 4px;
`;

const StatMetaLine = styled.div`
    display: flex;
    align-items: baseline;
    gap: 4px;
    margin-top: 1px;
`;

const StatValue = styled.span`
    font-size: 15px;
    font-weight: 700;
    color: ${({ theme }) => theme.textColor};
`;

const StatLabel = styled.span`
    font-size: 12px;
    color: ${({ theme }) => theme.infoTextColor};
`;

const StatMetaValue = styled.span`
    font-size: 13px;
    color: ${({ theme }) => theme.textColor};
`;

const StatSeparator = styled.span`
    color: ${({ theme }) => theme.infoTextColor};
`;
